import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Cross2Icon, ChevronLeftIcon, ChevronRightIcon, GridIcon,
} from '@radix-ui/react-icons';
import { Button, Image, Text, UnstyledButton } from '@/shared/ui';

/** Side of a strip thumbnail, and of a grid cell's row. */
const THUMB_SIZE = 56;

interface GalleryLightboxProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  /** Jumps straight to one photo, from the strip or the grid. */
  onSelect?: (index: number) => void;
}

/**
 * Full-screen image lightbox rendered into a portal. Shows the current image
 * with prev/next navigation, a close control, and a position indicator.
 *
 * @param {GalleryLightboxProps} props - Image list, current index, and navigation handlers.
 * @returns {React.ReactPortal} The rendered lightbox portal.
 */
export const GalleryLightbox: React.FC<GalleryLightboxProps> = ({
  images,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onSelect,
}) => {
  const { t } = useTranslation('features/properties');
  const [gridOpen, setGridOpen] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);

  /*
   * A lightbox that only answers the mouse is a lightbox somebody with a
   * keyboard cannot leave. Escape closes and the arrows step, which is what
   * every other full-screen viewer does — and what a person tries first.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (gridOpen) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); onPrev(); }
      if (event.key === 'ArrowRight') { event.preventDefault(); onNext(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, onPrev, onNext, gridOpen]);

  /* Keeps the current thumbnail in view as the arrows walk past its edge. */
  useEffect(() => {
    const strip = stripRef.current;
    const active = strip?.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [currentIndex, gridOpen]);

  const select = (index: number) => {
    onSelect?.(index);
    setGridOpen(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] bg-[var(--overlay-scrim-heavy)] flex items-center justify-center"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      role="dialog"
      aria-modal="true"
      aria-label={t('detailView.gallery.lightboxLabel')}
      onClick={onClose}
    >
      <Button
        variant="none"
        disableSize
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label={t('detailView.gallery.close')}
        className="fixed top-[calc(var(--space-4)+env(safe-area-inset-top,0px))] right-[var(--space-4)] z-[10051] bg-[color-mix(in_srgb,var(--surface-inverse)_60%,transparent)] border border-[color-mix(in_srgb,var(--text-inverse)_30%,transparent)] rounded-full w-11 h-11 flex items-center justify-center cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-inverse)_80%,transparent)] transition-[var(--transition-standard)] shadow-[var(--shadow-lg)]"
      >
        <Cross2Icon width={20} height={20} className="text-[var(--text-inverse)]" />
      </Button>

      {images.length > 1 && (
        <Button
          variant="none"
          disableSize
          data-testid="gallery-grid-toggle"
          aria-label={t('detailView.gallery.grid')}
          aria-pressed={gridOpen}
          onClick={(e) => { e.stopPropagation(); setGridOpen((open) => !open); }}
          className="fixed top-[calc(var(--space-4)+env(safe-area-inset-top,0px))] right-[calc(var(--space-4)+var(--space-12))] z-[10051] bg-[color-mix(in_srgb,var(--surface-inverse)_60%,transparent)] border border-[color-mix(in_srgb,var(--text-inverse)_30%,transparent)] rounded-full w-11 h-11 flex items-center justify-center cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-inverse)_80%,transparent)] transition-[var(--transition-standard)] shadow-[var(--shadow-lg)]"
        >
          <GridIcon width={18} height={18} className="text-[var(--text-inverse)]" />
        </Button>
      )}

      {currentIndex > 0 && (
        <Button
          variant="none"
          disableSize
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-[var(--space-4)] bg-[color-mix(in_srgb,var(--surface-inverse)_60%,transparent)] border border-[color-mix(in_srgb,var(--text-inverse)_30%,transparent)] rounded-full w-11 h-11 flex items-center justify-center cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-inverse)_80%,transparent)] transition-[var(--transition-standard)] shadow-[var(--shadow-lg)]"
        >
          <ChevronLeftIcon width={22} height={22} className="text-[var(--text-inverse)]" />
        </Button>
      )}

      {currentIndex < images.length - 1 && (
        <Button
          variant="none"
          disableSize
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-[var(--space-4)] bg-[color-mix(in_srgb,var(--surface-inverse)_60%,transparent)] border border-[color-mix(in_srgb,var(--text-inverse)_30%,transparent)] rounded-full w-11 h-11 flex items-center justify-center cursor-pointer hover:bg-[color-mix(in_srgb,var(--surface-inverse)_80%,transparent)] transition-[var(--transition-standard)] shadow-[var(--shadow-lg)]"
        >
          <ChevronRightIcon width={22} height={22} className="text-[var(--text-inverse)]" />
        </Button>
      )}

      <Image
        src={images[currentIndex]}
        alt={t('detailView.gallery.photoN', { index: currentIndex + 1 })}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-(--radius-md)"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />

      {/*
        The grid covers the photo rather than sitting beside it: at this size
        there is no arrangement where both are usable, and the grid exists to
        answer "which one do I want" — a question the current photo does not
        help with.
      */}
      {gridOpen ? (
        <div
          data-testid="gallery-grid"
          className="absolute inset-0 overflow-y-auto p-(--space-4) pt-[calc(var(--space-16)+env(safe-area-inset-top,0px))]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-3 gap-(--space-2) sm:grid-cols-4">
            {images.map((img, index) => (
              <UnstyledButton
                key={img}
                type="button"
                data-testid={`gallery-grid-item-${index}`}
                aria-label={t('detailView.gallery.photoN', { index: index + 1 })}
                aria-current={index === currentIndex}
                onClick={() => select(index)}
                className="aspect-square overflow-hidden rounded-(--radius-md)"
                style={{
                  outline: index === currentIndex ? '2px solid var(--text-inverse)' : 'none',
                  outlineOffset: -2,
                }}
              >
                <Image src={img} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover" />
              </UnstyledButton>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-(--space-2) pb-[calc(var(--space-3)+env(safe-area-inset-bottom,0px))]"
          onClick={(e) => e.stopPropagation()}
        >
          <Text className="m-0 text-[var(--text-inverse)] text-[length:var(--text-body-sm)]">
            {currentIndex + 1} / {images.length}
          </Text>

          {images.length > 1 && (
            <div
              ref={stripRef}
              data-testid="gallery-strip"
              className="flex max-w-full gap-(--space-2) overflow-x-auto px-(--space-4) [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {images.map((img, index) => (
                <UnstyledButton
                  key={img}
                  type="button"
                  data-testid={`gallery-strip-item-${index}`}
                  data-active={index === currentIndex}
                  aria-label={t('detailView.gallery.photoN', { index: index + 1 })}
                  aria-current={index === currentIndex}
                  onClick={() => select(index)}
                  className="flex-none overflow-hidden rounded-(--radius-sm)"
                  style={{
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                    /* The current one is the only one at full strength; the
                       rest recede so the strip reads as a position. */
                    opacity: index === currentIndex ? 1 : 0.5,
                    outline: index === currentIndex ? '2px solid var(--text-inverse)' : 'none',
                    outlineOffset: -2,
                  }}
                >
                  <Image src={img} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover" />
                </UnstyledButton>
              ))}
            </div>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
};
